import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from '../api/axios';
import { useAuth } from './AuthContext';

interface InvitationSummary {
  id: string;
  status: 'pending' | 'accepted' | 'rejected';
}

interface InvitationsContextType {
  pendingInvitations: number;
  refreshPendingInvitations: () => Promise<void>;
}

const InvitationsContext = createContext<InvitationsContextType | undefined>(undefined);

const countPendingInvitations = (invitations: InvitationSummary[]) => (
  invitations.filter((invitation) => invitation.status === 'pending').length
);

export const InvitationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [pendingInvitations, setPendingInvitations] = useState(0);

  const refreshPendingInvitations = async () => {
    if (!isAuthenticated) {
      setPendingInvitations(0);
      return;
    }

    try {
      const response = await axios.get('/invitations');
      setPendingInvitations(countPendingInvitations(response.data as InvitationSummary[]));
    } catch {
      setPendingInvitations(0);
    }
  };

  useEffect(() => {
    const syncPendingInvitations = async () => {
      if (!isAuthenticated) {
        setPendingInvitations(0);
        return;
      }

      try {
        const response = await axios.get('/invitations');
        setPendingInvitations(countPendingInvitations(response.data as InvitationSummary[]));
      } catch {
        setPendingInvitations(0);
      }
    };

    void syncPendingInvitations();
  }, [isAuthenticated]);

  return (
    <InvitationsContext.Provider
      value={{
        pendingInvitations,
        refreshPendingInvitations,
      }}
    >
      {children}
    </InvitationsContext.Provider>
  );
};

export const useInvitations = () => {
  const context = useContext(InvitationsContext);

  if (!context) {
    throw new Error('useInvitations must be used within InvitationsProvider');
  }

  return context;
};